import React, { useState, useEffect } from 'react';
import { EMZI_COLORS, EMZI_RADIUS, EMZI_SPACING, EMZI_TYPOGRAPHY } from '../styles/emziCI';

const LiveAgentPreview = ({ packType, formData, isMobile = false }) => {
  const [previewLang, setPreviewLang] = useState('en'); // 'en' or 'ms'
  const [isOpen, setIsOpen] = useState(false);

  // For mobile FAB
  const fabButtonStyle = {
    position: 'fixed',
    bottom: isMobile ? '24px' : '80px',
    right: '24px',
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    backgroundColor: EMZI_COLORS.warning, // Orange/Amber for Preview
    color: '#FFFFFF',
    border: 'none',
    cursor: 'pointer',
    fontSize: '24px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    zIndex: 999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const renderPreviewContent = () => {
    const content = formData.content || {};
    const name = formData.name?.[previewLang] || formData.name?.en || 'Untitled Pack';
    const description = formData.description?.[previewLang] || formData.description?.en || '';

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: EMZI_SPACING.md }}>
        {/* Pack Header */}
        <div
          style={{
            padding: EMZI_SPACING.md,
            backgroundColor: EMZI_COLORS.surface,
            borderRadius: EMZI_RADIUS.md,
            borderLeft: `4px solid ${EMZI_COLORS.warning}`,
          }}
        >
          <div style={{ fontSize: '10px', color: EMZI_COLORS.textMuted, textTransform: 'uppercase', marginBottom: '4px' }}>
            {packType} Pack
          </div>
          <h4 style={{ ...EMZI_TYPOGRAPHY.h4, margin: '0 0 8px 0', color: EMZI_COLORS.textPrimary }}>
            {name}
          </h4>
          {description && (
            <p style={{ fontSize: '12px', color: EMZI_COLORS.textSecondary, margin: 0 }}>
              {description}
            </p>
          )}
        </div>

        {/* Pack-Type Specific Content */}
        {renderPackSpecificContent(packType, content)}

        {/* Empty State */}
        {Object.keys(content).length === 0 && (
          <div
            style={{
              padding: EMZI_SPACING.lg,
              textAlign: 'center',
              color: EMZI_COLORS.textMuted,
              fontSize: '12px',
              fontStyle: 'italic',
            }}
          >
            Fill in the form to see live preview
          </div>
        )}
      </div>
    );
  };

  const renderPackSpecificContent = (type, content) => {
    switch (type) {
      case 'product':
        return (
          <>
            {content.key_features && content.key_features.length > 0 && (
              <PreviewSection title="Key Features">
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {content.key_features.map((feature, idx) => (
                    <li key={`li-${idx}`} style={{ fontSize: '12px', marginBottom: '4px' }}>
                      {feature[previewLang] || feature.en || feature}
                    </li>
                  ))}
                </ul>
              </PreviewSection>
            )}
            {content.benefits && content.benefits.length > 0 && (
              <PreviewSection title="Benefits">
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {content.benefits.map((benefit, idx) => (
                    <li key={`li-${idx}`} style={{ fontSize: '12px', marginBottom: '4px' }}>
                      {benefit[previewLang] || benefit.en || benefit}
                    </li>
                  ))}
                </ul>
              </PreviewSection>
            )}
            {content.pricing && (
              <PreviewSection title="Pricing">
                <p style={{ fontSize: '12px', margin: 0 }}>
                  {content.pricing[previewLang] || content.pricing.en || content.pricing}
                </p>
              </PreviewSection>
            )}
          </>
        );

      case 'promotion':
        return (
          <>
            {content.campaign_name && (
              <PreviewSection title="Campaign">
                <p style={{ fontSize: '12px', margin: 0 }}>
                  {content.campaign_name[previewLang] || content.campaign_name.en || content.campaign_name}
                </p>
              </PreviewSection>
            )}
            {content.offer_details && (
              <PreviewSection title="Offer">
                <p style={{ fontSize: '12px', margin: 0 }}>
                  {content.offer_details[previewLang] || content.offer_details.en || content.offer_details}
                </p>
              </PreviewSection>
            )}
            {(content.start_date || content.end_date) && (
              <PreviewSection title="Duration">
                <p style={{ fontSize: '12px', margin: 0 }}>
                  {content.start_date} - {content.end_date}
                </p>
              </PreviewSection>
            )}
          </>
        );

      case 'values':
        return (
          <>
            {content.core_values && content.core_values.length > 0 && (
              <PreviewSection title="Core Values">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {content.core_values.map((value, idx) => (
                    <span
                      key={`item-${idx}`}
                      style={{
                        padding: '4px 12px',
                        backgroundColor: EMZI_COLORS.surface,
                        borderRadius: EMZI_RADIUS.sm,
                        fontSize: '11px',
                        fontWeight: '500',
                      }}
                    >
                      {value[previewLang] || value.en || value}
                    </span>
                  ))}
                </div>
              </PreviewSection>
            )}
            {content.value_description && (
              <PreviewSection title="Description">
                <p style={{ fontSize: '12px', margin: 0 }}>
                  {content.value_description[previewLang] || content.value_description.en || content.value_description}
                </p>
              </PreviewSection>
            )}
          </>
        );

      case 'compliance':
        return (
          <>
            {content.regulation_type && (
              <PreviewSection title="Regulation">
                <p style={{ fontSize: '12px', margin: 0 }}>{content.regulation_type}</p>
              </PreviewSection>
            )}
            {content.dos && content.dos.length > 0 && (
              <PreviewSection title="✅ Do's">
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {content.dos.map((item, idx) => (
                    <li key={`li-${idx}`} style={{ fontSize: '12px', marginBottom: '4px', color: EMZI_COLORS.success }}>
                      {item[previewLang] || item.en || item}
                    </li>
                  ))}
                </ul>
              </PreviewSection>
            )}
            {content.donts && content.donts.length > 0 && (
              <PreviewSection title="⛔ Don'ts">
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {content.donts.map((item, idx) => (
                    <li key={`li-${idx}`} style={{ fontSize: '12px', marginBottom: '4px', color: EMZI_COLORS.error }}>
                      {item[previewLang] || item.en || item}
                    </li>
                  ))}
                </ul>
              </PreviewSection>
            )}
          </>
        );

      case 'ambassador':
        return (
          <>
            {content.ambassador_name && (
              <PreviewSection title="Ambassador">
                <p style={{ fontSize: '14px', fontWeight: '600', margin: 0 }}>{content.ambassador_name}</p>
                {content.role && (
                  <p style={{ fontSize: '11px', color: EMZI_COLORS.textMuted, margin: '4px 0 0 0' }}>
                    {content.role}
                  </p>
                )}
              </PreviewSection>
            )}
            {content.bio && (
              <PreviewSection title="Bio">
                <p style={{ fontSize: '12px', margin: 0 }}>
                  {content.bio[previewLang] || content.bio.en || content.bio}
                </p>
              </PreviewSection>
            )}
            {content.voice_tone && (
              <PreviewSection title="Voice & Tone">
                <p style={{ fontSize: '12px', margin: 0 }}>
                  {content.voice_tone[previewLang] || content.voice_tone.en || content.voice_tone}
                </p>
              </PreviewSection>
            )}
          </>
        );

      case 'health':
        return (
          <>
            {content.health_topic && (
              <PreviewSection title="Health Topic">
                <p style={{ fontSize: '12px', margin: 0 }}>
                  {content.health_topic[previewLang] || content.health_topic.en || content.health_topic}
                </p>
              </PreviewSection>
            )}
            {content.health_benefits && content.health_benefits.length > 0 && (
              <PreviewSection title="Health Benefits">
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {content.health_benefits.map((benefit, idx) => (
                    <li key={`li-${idx}`} style={{ fontSize: '12px', marginBottom: '4px' }}>
                      {benefit[previewLang] || benefit.en || benefit}
                    </li>
                  ))}
                </ul>
              </PreviewSection>
            )}
            {content.recommended_usage && (
              <PreviewSection title="Recommended Usage">
                <p style={{ fontSize: '12px', margin: 0 }}>
                  {content.recommended_usage[previewLang] || content.recommended_usage.en || content.recommended_usage}
                </p>
              </PreviewSection>
            )}
          </>
        );

      case 'direction':
        return (
          <>
            {content.strategic_direction && (
              <PreviewSection title="Strategic Direction">
                <p style={{ fontSize: '12px', margin: 0 }}>{content.strategic_direction}</p>
              </PreviewSection>
            )}
            {content.key_goals && content.key_goals.length > 0 && (
              <PreviewSection title="Key Goals">
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {content.key_goals.map((goal, idx) => (
                    <li key={`li-${idx}`} style={{ fontSize: '12px', marginBottom: '4px' }}>
                      {goal}
                    </li>
                  ))}
                </ul>
              </PreviewSection>
            )}
          </>
        );

      case 'services':
        return (
          <>
            {content.service_name && (
              <PreviewSection title="Service">
                <p style={{ fontSize: '12px', margin: 0 }}>
                  {content.service_name[previewLang] || content.service_name.en || content.service_name}
                </p>
              </PreviewSection>
            )}
            {content.service_features && content.service_features.length > 0 && (
              <PreviewSection title="Features">
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {content.service_features.map((feature, idx) => (
                    <li key={`li-${idx}`} style={{ fontSize: '12px', marginBottom: '4px' }}>
                      {feature[previewLang] || feature.en || feature}
                    </li>
                  ))}
                </ul>
              </PreviewSection>
            )}
          </>
        );

      case 'viral':
        return (
          <>
            {content.hook && (
              <PreviewSection title="Hook">
                <p style={{ fontSize: '12px', margin: 0 }}>{content.hook}</p>
              </PreviewSection>
            )}
            {content.format && (
              <PreviewSection title="Format">
                <p style={{ fontSize: '12px', margin: 0 }}>{content.format}</p>
              </PreviewSection>
            )}
            {content.platform && (
              <PreviewSection title="Platform">
                <p style={{ fontSize: '12px', margin: 0 }}>{content.platform}</p>
              </PreviewSection>
            )}
          </>
        );

      case 'trend':
        return (
          <>
            {content.trend_name && (
              <PreviewSection title="Trend">
                <p style={{ fontSize: '12px', margin: 0 }}>{content.trend_name}</p>
              </PreviewSection>
            )}
            {content.trend_indicators && content.trend_indicators.length > 0 && (
              <PreviewSection title="Indicators">
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {content.trend_indicators.map((indicator, idx) => (
                    <li key={`li-${idx}`} style={{ fontSize: '12px', marginBottom: '4px' }}>
                      {indicator}
                    </li>
                  ))}
                </ul>
              </PreviewSection>
            )}
          </>
        );

      default:
        return null;
    }
  };

  const PreviewSection = ({ title, children }) => (
    <div style={{ marginBottom: EMZI_SPACING.sm }}>
      <h5
        style={{
          ...EMZI_TYPOGRAPHY.caption,
          textTransform: 'uppercase',
          color: EMZI_COLORS.textMuted,
          marginBottom: EMZI_SPACING.xs,
          fontWeight: '600',
        }}
      >
        {title}
      </h5>
      {children}
    </div>
  );

  // Mobile View (FAB + Slide-up Sheet)
  if (isMobile) {
    return (
      <>
        {/* FAB Button */}
        <button onClick={() => setIsOpen(!isOpen)} style={fabButtonStyle}>
          👁️
        </button>

        {/* Slide-up Sheet */}
        {isOpen && (
          <>
            {/* Backdrop */}
            <div
              onClick={() => setIsOpen(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 1000,
                backdropFilter: 'blur(2px)',
              }}
            />

            {/* Sheet */}
            <div
              style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: EMZI_COLORS.background,
                borderTopLeftRadius: EMZI_RADIUS.lg,
                borderTopRightRadius: EMZI_RADIUS.lg,
                boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.15)',
                zIndex: 1001,
                maxHeight: '85vh',
                overflowY: 'auto',
                padding: EMZI_SPACING.lg,
                paddingBottom: 'calc(env(safe-area-inset-bottom) + 16px)',
              }}
            >
              {/* Header */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: EMZI_SPACING.md,
                }}
              >
                <h3 style={{ ...EMZI_TYPOGRAPHY.h3, margin: 0 }}>👁️ Live Preview</h3>
                <button
                  onClick={() => setIsOpen(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: EMZI_COLORS.textSecondary,
                    padding: 0,
                  }}
                >
                  ×
                </button>
              </div>

              {/* Language Toggle */}
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  marginBottom: EMZI_SPACING.md,
                  backgroundColor: EMZI_COLORS.surface,
                  borderRadius: EMZI_RADIUS.md,
                  padding: '4px',
                }}
              >
                <button
                  onClick={() => setPreviewLang('en')}
                  style={{
                    flex: 1,
                    padding: '8px',
                    backgroundColor: previewLang === 'en' ? EMZI_COLORS.primary : 'transparent',
                    color: previewLang === 'en' ? '#FFFFFF' : EMZI_COLORS.textSecondary,
                    border: 'none',
                    borderRadius: EMZI_RADIUS.sm,
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  🇬🇧 English
                </button>
                <button
                  onClick={() => setPreviewLang('ms')}
                  style={{
                    flex: 1,
                    padding: '8px',
                    backgroundColor: previewLang === 'ms' ? EMZI_COLORS.primary : 'transparent',
                    color: previewLang === 'ms' ? '#FFFFFF' : EMZI_COLORS.textSecondary,
                    border: 'none',
                    borderRadius: EMZI_RADIUS.sm,
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  🇲🇾 Malay
                </button>
              </div>

              {/* Preview Content */}
              {renderPreviewContent()}
            </div>
          </>
        )}
      </>
    );
  }

  // Desktop View (Panel)
  return (
    <div
      style={{
        backgroundColor: EMZI_COLORS.background,
        borderRadius: EMZI_RADIUS.lg,
        padding: EMZI_SPACING.md,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: EMZI_SPACING.md }}>
        <h3 style={{ ...EMZI_TYPOGRAPHY.h3, margin: '0 0 8px 0' }}>👁️ Live Preview</h3>
        <p style={{ fontSize: '11px', color: EMZI_COLORS.textSecondary, margin: 0 }}>
          See how agents will view this pack
        </p>
      </div>

      {/* Language Toggle */}
      <div
        style={{
          display: 'flex',
          gap: '8px',
          marginBottom: EMZI_SPACING.md,
          backgroundColor: EMZI_COLORS.surface,
          borderRadius: EMZI_RADIUS.md,
          padding: '4px',
        }}
      >
        <button
          onClick={() => setPreviewLang('en')}
          style={{
            flex: 1,
            padding: '8px',
            backgroundColor: previewLang === 'en' ? EMZI_COLORS.primary : 'transparent',
            color: previewLang === 'en' ? '#FFFFFF' : EMZI_COLORS.textSecondary,
            border: 'none',
            borderRadius: EMZI_RADIUS.sm,
            fontSize: '12px',
            fontWeight: '500',
            cursor: 'pointer',
          }}
        >
          🇬🇧 EN
        </button>
        <button
          onClick={() => setPreviewLang('ms')}
          style={{
            flex: 1,
            padding: '8px',
            backgroundColor: previewLang === 'ms' ? EMZI_COLORS.primary : 'transparent',
            color: previewLang === 'ms' ? '#FFFFFF' : EMZI_COLORS.textSecondary,
            border: 'none',
            borderRadius: EMZI_RADIUS.sm,
            fontSize: '12px',
            fontWeight: '500',
            cursor: 'pointer',
          }}
        >
          🇲🇾 MS
        </button>
      </div>

      {/* Preview Content (Scrollable) */}
      <div style={{ flex: 1, overflowY: 'auto' }}>{renderPreviewContent()}</div>
    </div>
  );
};

export default LiveAgentPreview;
